'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

interface PerformanceMetrics {
  on_time_arrival: number;
  on_time_completion: number;
  cost_accuracy: number;
}
export interface CrewForRow2 {
  id: string;
  company_name: string;
  foreman_name: string;
  compliance_status: string;
  customer_rating: number;
  performance_metrics: PerformanceMetrics;
}

interface Props {
  crews: CrewForRow2[];
}

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

// ─── Widget 3: Crew Performance Leaderboard (real data) ──────────────────
// Composite score formula (0-100):
//   on_time_arrival_pct    * 0.30
// + on_time_completion_pct * 0.30
// + cost_accuracy_pct      * 0.20
// + customer_rating * 20   * 0.20   (customer_rating is 0-5; * 20 → 0-100)
// Note: prior-week delta deliberately omitted — installer_crews has no
// historical performance snapshots today. When a historical table lands,
// the delta arrow can be added without changing the composite formula.

function computeComposite(c: CrewForRow2): number {
  const pm = c.performance_metrics ?? { on_time_arrival: 0, on_time_completion: 0, cost_accuracy: 0 };
  return (
    (pm.on_time_arrival || 0)    * 0.30 +
    (pm.on_time_completion || 0) * 0.30 +
    (pm.cost_accuracy || 0)      * 0.20 +
    (c.customer_rating || 0) * 20 * 0.20
  );
}

const MEDAL = ['🥇', '🥈', '🥉'];

function LeaderboardRow({ rankIcon, name, score }: { rankIcon: string; name: string; score: number }) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-2"
      style={{ border: '1px solid #F3F4F6' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm flex-shrink-0">{rankIcon}</span>
        <p className="text-xs font-semibold text-gray-800 truncate">{name}</p>
      </div>
      <span className="text-sm font-bold flex-shrink-0" style={{ color: '#000150' }}>
        {score.toFixed(1)}
      </span>
    </div>
  );
}

function CrewPerformanceLeaderboard({ crews }: { crews: CrewForRow2[] }) {
  // Restrict to active (i.e., not 'onboarding') crews — onboarding crews
  // have zeroed-out performance metrics and would spuriously dominate
  // the Bottom 3.
  const active = crews.filter((c) => c.compliance_status !== 'onboarding');
  const scored = active.map((c) => ({ name: c.company_name, foreman: c.foreman_name, score: computeComposite(c) }));
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  const top    = sorted.slice(0, 3);
  const bottom = sorted.slice(-3).reverse();
  const avg = scored.length > 0 ? scored.reduce((s, c) => s + c.score, 0) / scored.length : 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <div className="flex items-center gap-2 mb-1">
        <Icon name="TrophyIcon" size={15} style={{ color: '#F59E0B' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Crew Performance
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        WTD composite score · network avg {avg.toFixed(1)}
      </p>
      {scored.length === 0 ? (
        <div
          className="rounded-lg p-3 flex items-center gap-2"
          style={{ background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.18)' }}
        >
          <Icon name="DocumentTextIcon" size={16} style={{ color: '#6B7280' }} />
          <p className="text-xs font-medium text-gray-600">No active crews yet — onboarding crews are excluded</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p
              className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"
              style={{ color: '#047857' }}
            >
              <span>🏆</span> Top 3
            </p>
            <div className="space-y-1.5">
              {top.map((c, i) => (
                <LeaderboardRow key={`top-${c.name}-${i}`} rankIcon={MEDAL[i]} name={c.name} score={c.score} />
              ))}
            </div>
          </div>
          <div>
            <p
              className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"
              style={{ color: '#B91C1C' }}
            >
              <span>⚠</span> Bottom 3
            </p>
            <div className="space-y-1.5">
              {bottom.map((c, i) => (
                <LeaderboardRow key={`bot-${c.name}-${i}`} rankIcon="⚠" name={c.name} score={c.score} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Widget 4: Active Jobs by Crew (mock) ────────────────────────────────

interface ActiveJobRow {
  crewName: string;
  jobCount: number;
  totalInFlight: number;
}

const MOCK_ACTIVE_JOBS: ActiveJobRow[] = [];

function formatMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function ActiveJobsByCrew() {
  const total = MOCK_ACTIVE_JOBS.reduce((s, r) => s + r.totalInFlight, 0);
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="BriefcaseIcon" size={15} style={{ color: '#033494' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Active Jobs by Crew
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold whitespace-nowrap">
          {MOCK_ACTIVE_JOBS.length} crews · {formatMoney(total)} in flight
        </p>
      </div>
      <div className="space-y-2">
        {MOCK_ACTIVE_JOBS.map((r, i) => (
          <div
            key={`${r.crewName}-${i}`}
            className="flex items-center justify-between gap-2 rounded-lg px-3 py-2"
            style={{ border: '1px solid #F3F4F6' }}
          >
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{r.crewName}</p>
              <p className="text-[10px] text-gray-500">
                {r.jobCount} active {r.jobCount === 1 ? 'job' : 'jobs'} · {formatMoney(r.totalInFlight)} in flight
              </p>
            </div>
            <button
              onClick={() => console.log('[InstallerNetwork] View jobs (no-op):', r.crewName)}
              className="rounded-md px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 hover:opacity-90 active:scale-95 transition"
              style={{ background: 'rgba(3,52,148,0.08)', color: '#033494' }}
              suppressHydrationWarning
            >
              View jobs
              <Icon name="ArrowRightIcon" size={10} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Row export ───────────────────────────────────────────────────────────

export default function Row2PerformanceAndJobs({ crews }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <CrewPerformanceLeaderboard crews={crews} />
      <ActiveJobsByCrew />
    </section>
  );
}
